import { InputAdornment, Button, IconButton, TableBody, TableHead, TextField, Typography } from '@mui/material';
import {
  CustomBodyCell,
  CustomHeaderCell,
  CustomTable,
  CustomTableContainer,
  CustomTableRow,
} from '@components/Styles/TableStyle';
import {
  CustomStack,
  CustomTextField,
  CustomAutocomplete,
  RequiredMark,
  RequiredMarkBox,
  SectionWrapper,
} from '@components/Styles/CommonStyle';
import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import ko from 'date-fns/locale/ko';
import DatePicker from 'react-datepicker';
import React from 'react';
import moment from 'moment';
import { theme } from '@components/Styles/theme';
import { formatAmount } from '@utils/formatter';
import api from '@api/axios';
import CustomInputDate from '@components/common/Input/CustomInputDate';

const ProjectCreateTableInfoV2 = forwardRef(
  ({ setStartYearFinance, setEndYearFinance, setSelectedYearFinance }, ref) => {
    const [projectInfo, setProjectInfo] = useState({
      name: '', // 사업명
      amount: '', // 사업금액
      projectManager: '', // 사업담당자
      orderingCompany: '', // 발주사
      accountCompany: '', // 거래처
      startDate: moment().format('YYYY.MM.DD'), // 사업시작일
      endDate: moment().format('YYYY.MM.DD'), // 사업종료일
    });

    const [searchOrderingCompanyList, setSearchOrderingCompanyList] = useState([]);
    const [searchAccountCompanyList, setSearchAccountCompanyList] = useState([]);

    useImperativeHandle(ref, () => ({
      getProjectInfo: () => projectInfo,
      setProjectInfo: (info) => setProjectInfo(info),
    }));

    // 사업정보 변경
    const handleProjectInfoChange = (e) => {
      let { name, value } = e.target;

      // 사업금액 경우
      if (name === 'amount') {
        value = value.replace(/[^0-9]/g, '');
      }

      setProjectInfo({ ...projectInfo, [name]: value });
    };

    // 발주사 검색
    const onSearchOrderingCompany = (name) => {
      const accountType = '법인사업자';

      api
        .get(
          `/api/account/readAccountList?${accountType ? `&accountType=${accountType}` : ''}${name ? `&name=${name}` : ''}`,
        )
        .then((res) => {
          const list = res.data.result.rows;

          const orderingCompanyList = list.map((item) => {
            return item.company_name;
          });

          setSearchOrderingCompanyList(orderingCompanyList);
        });
    };

    // 거래처 검색
    const onSearchAccountCompany = (name) => {
      const accountType = '법인사업자';

      api
        .get(
          `/api/account/readAccountList?${accountType ? `&accountType=${accountType}` : ''}${name ? `&name=${name}` : ''}`,
        )
        .then((res) => {
          const list = res.data.result.rows;

          const accountCompanyList = list.map((item) => {
            return item.company_name;
          });

          setSearchAccountCompanyList(accountCompanyList);
        });
    };

    useEffect(() => {
      onSearchOrderingCompany(projectInfo.orderingCompany);
      onSearchAccountCompany(projectInfo.accountCompany);
    }, [projectInfo.orderingCompany, projectInfo.accountCompany]);

    return (
      <React.Fragment>
        <CustomStack mb={2}>
          <Typography sx={theme.typography.h4}>사업정보 등록</Typography>
        </CustomStack>

        {/* 사업정보 */}
        <SectionWrapper>
          <CustomStack justifyContent="spaceBetween" mb={2}>
            <Typography sx={theme.typography.h4}>사업 정보</Typography>
            <RequiredMarkBox>
              <RequiredMark />
              (&nbsp;&nbsp;) 필수입력사항
            </RequiredMarkBox>
          </CustomStack>

          <CustomTableContainer>
            <CustomTable fourColumn>
              <TableBody>
                <CustomTableRow>
                  <CustomHeaderCell>
                    <RequiredMark />
                    사업명
                  </CustomHeaderCell>
                  <CustomBodyCell>
                    <CustomTextField
                      size="small"
                      value={projectInfo.name}
                      onChange={handleProjectInfoChange}
                      name="name"
                    />
                  </CustomBodyCell>
                  <CustomHeaderCell>
                    <RequiredMark />
                    사업금액 (VAT 별도)
                  </CustomHeaderCell>
                  <CustomBodyCell>
                    <CustomTextField
                      size="small"
                      right
                      value={formatAmount(projectInfo.amount)}
                      onChange={handleProjectInfoChange}
                      name="amount"
                    />
                  </CustomBodyCell>
                </CustomTableRow>
                <CustomTableRow>
                  <CustomHeaderCell>
                    <RequiredMark />
                    사업시작일
                  </CustomHeaderCell>
                  <CustomBodyCell>
                    <DatePicker
                      locale={ko}
                      selected={projectInfo.startDate}
                      dateFormat="yyyy.M.d"
                      onChange={(date) => {
                        if (date) {
                          const formattedDate = moment(date).format('YYYY-MM-DD');
                          setProjectInfo({ ...projectInfo, startDate: formattedDate });
                          setStartYearFinance(moment(formattedDate).year());
                          setSelectedYearFinance(moment(formattedDate).year());
                        } else {
                          setProjectInfo({ ...projectInfo, startDate: '' });
                        }
                      }}
                      customInput={<CustomInputDate />}
                    />
                  </CustomBodyCell>
                  <CustomHeaderCell>
                    <RequiredMark />
                    사업종료일
                  </CustomHeaderCell>
                  {/* 날짜 이외에는 값을 받지 못 하게 */}
                  <CustomBodyCell>
                    <DatePicker
                      locale={ko}
                      selected={projectInfo.endDate ? new Date(projectInfo.endDate) : null}
                      dateFormat="yyyy.M.d"
                      onChange={(date) => {
                        // date는 Date 객체이거나 null입니다.
                        if (date) {
                          const formattedDate = moment(date).format('YYYY-MM-DD');
                          setProjectInfo({ ...projectInfo, endDate: formattedDate });
                          setEndYearFinance(moment(formattedDate).year());
                          setSelectedYearFinance(moment(formattedDate).year());
                        } else {
                          setProjectInfo({ ...projectInfo, endDate: '' });
                        }
                      }}
                      customInput={<CustomInputDate />}
                    />
                  </CustomBodyCell>
                </CustomTableRow>
                <CustomTableRow>
                  <CustomHeaderCell>
                    <RequiredMark />
                    발주사
                  </CustomHeaderCell>
                  <CustomBodyCell>
                    <CustomAutocomplete
                      disableClearable
                      value={projectInfo.orderingCompany}
                      options={searchOrderingCompanyList}
                      onChange={(event, value) => {
                        if (value) {
                          setProjectInfo({ ...projectInfo, orderingCompany: value });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onChange={(e) => {
                            setProjectInfo({ ...projectInfo, orderingCompany: e.target.value });
                          }}
                        />
                      )}
                    />
                  </CustomBodyCell>
                  <CustomHeaderCell>
                    <RequiredMark />
                    거래처
                  </CustomHeaderCell>
                  <CustomBodyCell>
                    <CustomAutocomplete
                      disableClearable
                      value={projectInfo.accountCompany}
                      options={searchAccountCompanyList}
                      onChange={(event, value) => {
                        if (value) {
                          setProjectInfo({ ...projectInfo, accountCompany: value });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onChange={(e) => {
                            setProjectInfo({ ...projectInfo, accountCompany: e.target.value });
                          }}
                        />
                      )}
                    />
                  </CustomBodyCell>
                </CustomTableRow>
                <CustomTableRow>
                  <CustomHeaderCell>
                    <RequiredMark />
                    사업담당자
                  </CustomHeaderCell>
                  <CustomBodyCell colSpan={3}>
                    <CustomTextField
                      size="small"
                      value={projectInfo.projectManager}
                      onChange={handleProjectInfoChange}
                      name="projectManager"
                    />
                  </CustomBodyCell>
                </CustomTableRow>
              </TableBody>
            </CustomTable>
          </CustomTableContainer>
        </SectionWrapper>
      </React.Fragment>
    );
  },
);

export default ProjectCreateTableInfoV2;
